import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useCampaign } from '../../context/CampaignContext';
import { isGameMaster, cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Dices, Send, Sparkles, Star, RotateCcw, Trash2, X, Box,
} from 'lucide-react';
import Dice3DOverlay from './Dice3DOverlay';
import {
  addVisualDiceToRoll,
  combineDiceNotations,
  countDiceInNotation,
  rollVisualDice,
  stopDiceRoll,
} from './diceBoxEngine';

const QUICK_ROLLS = [
  { label: 'd4',     notation: '1d4'     },
  { label: 'd6',     notation: '1d6'     },
  { label: 'd8',     notation: '1d8'     },
  { label: 'd10',    notation: '1d10'    },
  { label: 'd12',    notation: '1d12'    },
  { label: 'd20',    notation: '1d20'    },
  { label: 'd100',   notation: '1d100'   },
  { label: '2d6',    notation: '2d6'     },
  { label: '4d6kh3', notation: '4d6kh3'  },
  { label: 'adv',    notation: 'adv'     },
  { label: 'dis',    notation: 'dis'     },
];

export const DiceRollerPanel = () => {
  const [open, setOpen]         = useState(false);
  const [notation, setNotation] = useState('');
  const [label, setLabel]       = useState('');
  const [secret, setSecret]     = useState(false);
  const [history, setHistory]   = useState([]);
  const [lastRoll, setLastRoll] = useState(null);
  const [rolling, setRolling]   = useState(false);
  const [presets, setPresets]    = useState([]);
  const [rollKey, setRollKey]   = useState(0);
  const [anim3D, setAnim3D]     = useState(() => localStorage.getItem('dice3d') !== 'false');
  const [overlay3D, setOverlay3D] = useState(null);
  const [acceptingAdditions, setAcceptingAdditions] = useState(false);
  const inputRef = useRef(null);
  const activeRollRef = useRef(null);

  const { isAuthenticated, user, language } = useAuth();
  const { currentCampaign } = useCampaign();
  const location = useLocation();

  const campaignMatch =
    matchPath({ path: '/campaigns/:campaignId/*' }, location.pathname)
    || matchPath({ path: '/campaigns/:campaignId' }, location.pathname);
  const campaignId = campaignMatch?.params?.campaignId;

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);
  const lang = language;

  // Track sidebar pin state so the dice button follows the sidebar width
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    try { return localStorage.getItem('sidebar-pinned') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'sidebar-pinned') {
        setSidebarPinned(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', onStorage);

    // Also listen for same-tab changes via a custom event
    const onSidebarChange = () => {
      try { setSidebarPinned(localStorage.getItem('sidebar-pinned') === 'true'); } catch {}
    };
    window.addEventListener('sidebar-pinned-change', onSidebarChange);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('sidebar-pinned-change', onSidebarChange);
    };
  }, []);

  // Keyboard shortcut: Ctrl+D
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ──── Data fetching ────

  const fetchHistory = useCallback(async () => {
    if (!campaignId) return;
    try {
      const res = await axios.get(`/api/campaigns/${campaignId}/dice/history`, {
        params: { limit: 30 },
      });
      setHistory(res.data);
    } catch {
      // silent
    }
  }, [campaignId]);

  const fetchPresets = useCallback(async () => {
    if (!campaignId) return;
    try {
      const res = await axios.get(`/api/campaigns/${campaignId}/dice/presets`);
      setPresets(res.data);
    } catch {
      // silent
    }
  }, [campaignId]);

  useEffect(() => {
    if (open && campaignId) {
      fetchHistory();
      fetchPresets();
    }
  }, [open, campaignId, fetchHistory, fetchPresets]);

  // ──── 3D toggle ────

  const toggle3D = (val) => {
    setAnim3D(val);
    localStorage.setItem('dice3d', val ? 'true' : 'false');
  };

  // ──── Roll dice ────

  const rollDice = useCallback(async (notationStr, rollLabel) => {
    if (!campaignId || !notationStr.trim()) return;
    const cleanNotation = notationStr.trim();

    const activeRoll = activeRollRef.current;
    if (activeRoll) {
      if (activeRoll.phase === 'physical') {
        const addition = addVisualDiceToRoll(cleanNotation);
        if (addition.accepted) {
          activeRoll.notation = addition.notation;
          activeRoll.diceCount = addition.diceCount;
          setOverlay3D((current) => current?.pending ? {
            ...current,
            pendingDiceCount: addition.diceCount,
            pendingNotation: addition.notation,
          } : current);
          setNotation('');
          return;
        }
      }
      return;
    }

    const rollContext = {
      phase: anim3D ? 'physical' : 'submitting',
      notation: combineDiceNotations([cleanNotation]),
      diceCount: countDiceInNotation(cleanNotation),
      label: rollLabel || label || '',
      secret,
    };
    activeRollRef.current = rollContext;
    setRolling(true);
    let visualRolls = null;

    if (anim3D) {
      setAcceptingAdditions(true);
      setOverlay3D({
        pending: true,
        rollData: null,
        pendingDiceCount: rollContext.diceCount,
        pendingNotation: rollContext.notation,
      });
      try {
        const visualResult = await rollVisualDice(cleanNotation);
        setAcceptingAdditions(false);
        if (visualResult.cancelled) {
          setOverlay3D(null);
          setRolling(false);
          if (activeRollRef.current === rollContext) activeRollRef.current = null;
          return;
        }
        visualRolls = visualResult.visualRolls;
        rollContext.notation = visualResult.notation || rollContext.notation;
        rollContext.phase = 'submitting';
      } catch (error) {
        console.error('Unable to complete physical dice roll:', error);
        stopDiceRoll();
        setOverlay3D(null);
        setRolling(false);
        setAcceptingAdditions(false);
        if (activeRollRef.current === rollContext) activeRollRef.current = null;
        toast.error(lang === 'fr'
          ? 'Le lancer physique a échoué. Aucun résultat n’a été enregistré.'
          : 'The physical roll failed. No result was recorded.');
        return;
      }
    }

    try {
      const res = await axios.post(`/api/campaigns/${campaignId}/dice/roll`, {
        notation: rollContext.notation,
        label:    rollContext.label,
        secret:   rollContext.secret,
        visual_rolls: visualRolls,
      });
      setLastRoll(res.data);
      setRollKey(k => k + 1);
      setHistory(prev => [res.data, ...prev.slice(0, 29)]);
      setNotation('');
      setLabel('');
      // The result reveal uses the same physical face values accepted by the API.
      if (anim3D && res.data.rolls?.length > 0) {
        setOverlay3D({ pending: false, rollData: res.data });
      }
    } catch (error) {
      stopDiceRoll();
      setOverlay3D(null);
      toast.error(error.response?.data?.detail || (lang === 'fr'
        ? 'Impossible d’enregistrer le lancer.'
        : 'Unable to save the roll.'));
    } finally {
      if (activeRollRef.current === rollContext) activeRollRef.current = null;
      setAcceptingAdditions(false);
      setRolling(false);
    }
  }, [campaignId, label, secret, anim3D, lang]);

  const handleSubmit = (e) => {
    e.preventDefault();
    rollDice(notation);
  };

  // ──── Presets (favorites) ────

  const savePreset = async (notationStr, presetLabel) => {
    if (!campaignId || !notationStr) return;
    try {
      const res = await axios.post(`/api/campaigns/${campaignId}/dice/presets`, {
        notation: notationStr,
        label: presetLabel || notationStr,
      });
      setPresets(prev => {
        if (prev.some(p => p.id === res.data.id)) return prev;
        return [res.data, ...prev];
      });
    } catch {
      // silent
    }
  };

  const deletePreset = async (presetId) => {
    if (!campaignId) return;
    try {
      await axios.delete(`/api/campaigns/${campaignId}/dice/presets/${presetId}`);
      setPresets(prev => prev.filter(p => p.id !== presetId));
    } catch {
      // silent
    }
  };

  const isPresetSaved = (notationStr) => {
    return presets.some(p => p.notation === notationStr?.trim().toLowerCase());
  };

  const clearLocalHistory = () => {
    setHistory([]);
    setLastRoll(null);
  };

  // ──── Guard ────

  if (!isAuthenticated || !campaignId || !currentCampaign || currentCampaign.id !== campaignId) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="dice-roller-trigger"
          aria-label={lang === 'fr' ? 'Ouvrir le lanceur de dés (Ctrl+D)' : 'Open dice roller (Ctrl+D)'}
          className="dice-trigger-btn"
          style={{ '--sidebar-w': sidebarPinned ? '240px' : '52px' }}
        >
          <Dices className="h-4 w-4 shrink-0" />
        </button>
      </SheetTrigger>

      <SheetContent side="left" className="w-full sm:w-[420px] p-0 flex flex-col">
        {/* ── Header ── */}
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border/60 shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-cinzel flex items-center gap-2 text-base">
              <Dices className="h-5 w-5" />
              {lang === 'fr' ? 'Lanceur de Dés' : 'Dice Roller'}
            </SheetTitle>
            {/* 3D animation toggle */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => toggle3D(!anim3D)}
                    className={cn(
                      'h-8 w-8 rounded-lg flex items-center justify-center transition-all',
                      anim3D
                        ? 'bg-primary/15 text-primary border border-primary/30'
                        : 'text-muted-foreground hover:bg-muted/60 border border-transparent'
                    )}
                  >
                    <Box className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {anim3D
                    ? (lang === 'fr' ? 'Désactiver animation 3D' : 'Disable 3D animation')
                    : (lang === 'fr' ? 'Activer animation 3D' : 'Enable 3D animation')
                  }
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-[10px] text-muted-foreground">
            {lang === 'fr' ? 'Raccourci : Ctrl+D' : 'Shortcut: Ctrl+D'}
          </p>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="px-5 pt-4 pb-5 space-y-4">

            {/* ── Favoris ── */}
            {presets.length > 0 && (
              <div>
                <p className="text-[10px] font-bold text-amber-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Star className="h-3 w-3 fill-amber-500" />
                  {lang === 'fr' ? 'Favoris' : 'Favorites'}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {presets.map(preset => (
                    <div key={preset.id} className="group/preset relative flex items-center">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs pr-7 border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10 hover:border-amber-500/40 transition-all active:scale-95 h-8"
                        onClick={() => rollDice(preset.notation, preset.label)}
                        disabled={rolling && !acceptingAdditions}
                      >
                        <Star className="h-3 w-3 mr-1 fill-amber-500 text-amber-500 shrink-0" />
                        <span className="font-medium truncate max-w-[90px]">{preset.label}</span>
                        {preset.notation !== preset.label && (
                          <span className="text-muted-foreground font-mono ml-1 text-[10px]">
                            {preset.notation}
                          </span>
                        )}
                      </Button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deletePreset(preset.id); }}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full flex items-center justify-center opacity-0 group-hover/preset:opacity-100 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Jets rapides ── */}
            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                {lang === 'fr' ? 'Jets rapides' : 'Quick rolls'}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_ROLLS.map((qr) => (
                  <Button
                    key={qr.notation}
                    variant="outline"
                    size="sm"
                    className="text-xs font-mono h-8 px-2.5 hover:bg-primary/10 hover:border-primary/30 active:scale-95 transition-all"
                    onClick={() => rollDice(qr.notation, qr.label)}
                    disabled={rolling && !acceptingAdditions}
                  >
                    {qr.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* ── Jet personnalisé ── */}
            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                {lang === 'fr' ? 'Jet personnalisé' : 'Custom roll'}
              </p>
              <form onSubmit={handleSubmit} className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    placeholder="2d6+3, 1d20, 4d6kh3, adv…"
                    value={notation}
                    onChange={(e) => setNotation(e.target.value)}
                    className="flex-1 font-mono h-9"
                    data-testid="dice-notation-input"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    className="h-9 w-9"
                    disabled={(rolling && !acceptingAdditions) || !notation.trim()}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder={lang === 'fr' ? 'Label (optionnel)' : 'Label (optional)'}
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="flex-1 text-xs h-8"
                  />
                  {isMJ && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Switch
                        id="secret-roll"
                        checked={secret}
                        onCheckedChange={setSecret}
                        className="scale-75"
                      />
                      <Label
                        htmlFor="secret-roll"
                        className="text-[10px] text-muted-foreground cursor-pointer"
                      >
                        Secret
                      </Label>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* ── Résultat du dernier jet ── */}
            {lastRoll && (
              <div
                key={rollKey}
                className={cn(
                  'rounded-xl border p-4 text-center space-y-1.5 transition-all animate-dice-roll-in relative',
                  lastRoll.is_critical
                    ? 'border-yellow-500/60 bg-yellow-500/10'
                    : lastRoll.is_fumble
                    ? 'border-destructive/60 bg-destructive/10'
                    : 'border-border bg-muted/20'
                )}
              >
                {/* Save as favorite button */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => savePreset(lastRoll.notation, lastRoll.label || lastRoll.notation)}
                        disabled={isPresetSaved(lastRoll.notation)}
                        className={cn(
                          'absolute top-2 right-2 h-7 w-7 rounded-lg flex items-center justify-center transition-all',
                          isPresetSaved(lastRoll.notation)
                            ? 'text-amber-500 bg-amber-500/10'
                            : 'text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10'
                        )}
                      >
                        <Star className={cn('h-4 w-4', isPresetSaved(lastRoll.notation) && 'fill-amber-500')} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="text-xs">
                      {isPresetSaved(lastRoll.notation)
                        ? (lang === 'fr' ? 'Déjà en favori' : 'Already saved')
                        : (lang === 'fr' ? 'Sauvegarder en favori' : 'Save as favorite')
                      }
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <div className={cn(
                  'text-4xl font-bold font-cinzel tabular-nums',
                  lastRoll.is_critical && 'animate-dice-shake text-yellow-500',
                  lastRoll.is_fumble && 'animate-dice-shake text-destructive',
                )}>
                  {lastRoll.total}
                  {lastRoll.is_critical && (
                    <Sparkles className="inline ml-2 h-6 w-6 text-yellow-500" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground font-mono">{lastRoll.breakdown}</p>
                {lastRoll.label && (
                  <p className="text-xs font-medium text-foreground/80">{lastRoll.label}</p>
                )}
                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                  {lastRoll.is_critical && (
                    <Badge className="bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/40 text-[10px]">
                      {lang === 'fr' ? 'CRITIQUE !' : 'CRITICAL!'}
                    </Badge>
                  )}
                  {lastRoll.is_fumble && (
                    <Badge variant="destructive" className="text-[10px]">
                      FUMBLE
                    </Badge>
                  )}
                  {lastRoll.secret && (
                    <Badge variant="outline" className="text-[10px]">
                      {lang === 'fr' ? 'Jet secret' : 'Secret roll'}
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* ── Historique ── */}
          <div className="px-5 py-3 flex items-center justify-between">
            <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
              {lang === 'fr' ? 'Historique' : 'History'}
            </h4>
            <div className="flex items-center gap-2">
              {history.length > 0 && (
                <span className="text-[10px] text-muted-foreground">
                  {history.length} {lang === 'fr' ? 'jets' : 'rolls'}
                </span>
              )}
              {history.length > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={clearLocalHistory}
                        className="h-5 w-5 rounded flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="text-xs">
                      {lang === 'fr' ? 'Vider l\'affichage' : 'Clear display'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          <div className="px-5 pb-5">
            <div className="space-y-1">
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8 opacity-60">
                  {lang === 'fr' ? 'Aucun jet effectué' : 'No rolls yet'}
                </p>
              ) : (
                history.map((roll) => (
                  <div
                    key={roll.id}
                    className={cn(
                      'group flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm transition-colors',
                      roll.is_critical
                        ? 'bg-yellow-500/5 border border-yellow-500/20'
                        : roll.is_fumble
                        ? 'bg-destructive/5 border border-destructive/20'
                        : 'hover:bg-muted/40 border border-transparent'
                    )}
                  >
                    {/* User */}
                    <span className="text-[10px] text-muted-foreground w-14 shrink-0 truncate font-medium">
                      {roll.username || '?'}
                    </span>

                    {/* Notation */}
                    <span className="text-xs text-muted-foreground font-mono flex-1 truncate">
                      {roll.notation}
                    </span>

                    {/* Label */}
                    {roll.label && (
                      <span className="text-[10px] text-muted-foreground/60 italic truncate max-w-[60px] hidden sm:inline">
                        {roll.label}
                      </span>
                    )}

                    {/* Action buttons (visible on hover) */}
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      {/* Reroll */}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => rollDice(roll.notation, roll.label)}
                              disabled={rolling && !acceptingAdditions}
                              className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                            >
                              <RotateCcw className="h-3 w-3" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            {lang === 'fr' ? 'Relancer' : 'Reroll'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      {/* Save as favorite */}
                      {!isPresetSaved(roll.notation) && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => savePreset(roll.notation, roll.label || roll.notation)}
                                className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10 transition-colors"
                              >
                                <Star className="h-3 w-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              {lang === 'fr' ? 'Sauvegarder' : 'Save'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>

                    {/* Result */}
                    <span className="ml-auto font-bold tabular-nums shrink-0 text-sm">
                      {roll.total}
                    </span>

                    {/* Status indicators */}
                    {roll.is_critical && (
                      <span className="text-yellow-500 text-xs shrink-0">★</span>
                    )}
                    {roll.is_fumble && (
                      <span className="text-destructive text-xs shrink-0">✗</span>
                    )}
                    {roll.secret && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0">
                        secret
                      </Badge>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </ScrollArea>
      </SheetContent>

      {/* ── 3D Dice Overlay ── */}
      {overlay3D && (
        <Dice3DOverlay
          rollData={overlay3D.rollData}
          pending={overlay3D.pending}
          pendingDiceCount={overlay3D.pendingDiceCount}
          pendingNotation={overlay3D.pendingNotation}
          onAddDice={rollDice}
          onComplete={() => setOverlay3D(null)}
        />
      )}
    </Sheet>
  );
};

export default DiceRollerPanel;
